import classNames from 'classnames';
import * as _ from 'lodash-es';
import numeral from 'numeral';
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { SetNonNullable, SetRequired } from 'type-fest';
import Avatar from '../components/Avatar';
import FloatingCard from '../components/FloatingCard';
import HoverView from '../components/HoverView';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { useGetUser } from '../user/useAllUsers';
import Link from '../utils/router/Link';
import userPath from '../utils/url/userPath';
import * as styles from './PostEditorsChart.module.scss';

const AVATAR_SIZE = parseInt(styles.avatarSize, 10);

const formatNumEdits = (value: number) =>
  value >= 1000 ? numeral(value).format('0.0a').toUpperCase() : value;

const POST_INSIGHTS_EDIT_FRAGMENT = gql(`
  fragment PostEditorsChart_postInsightsEdit on PostInsightsEdit {
    user {
      ...useGetUser_user
    }
    numEdits
  }
`);

interface AdditionalEditorsTooltipContentProps {
  edits: DocumentType<typeof POST_INSIGHTS_EDIT_FRAGMENT>[];
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const AdditionalEditorsTooltipContent = ({
  edits,
}: AdditionalEditorsTooltipContentProps) => {
  const getUser = useGetUser();
  return (
    <div className={styles.additionalEditorsContainer}>
      <div className={styles.inner}>
        <div className={styles.title}>Additional editors</div>
        {edits.map((edit) => {
          if (!edit.user) return null;

          const user = getUser(edit.user);

          return (
            <div className={styles.additionalEditorsRow} key={user.id}>
              <Avatar user={user} size={16} />
              <div className={styles.userName}>{user.name}</div>
              {!!edit.numEdits && (
                <div className={styles.numEdits}>
                  {formatNumEdits(edit.numEdits)}
                </div>
              )}
            </div>
          );
        })}
        {edits.length < 3 && (
          <div className={styles.infoText}>
            These users made minor edits to the post.
          </div>
        )}
      </div>
    </div>
  );
};

interface GroupCountProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
}

const GroupCount = React.forwardRef<HTMLDivElement, GroupCountProps>(
  ({ value, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames(styles.groupCount, className)}
        {...rest}
      >
        <div className={styles.value}>{formatNumEdits(value)}</div>
      </div>
    );
  },
);

interface RowProps {
  avatar: React.ReactNode;
  numEdits: number;
  percentage: number;
}

const Row = ({ avatar, percentage, numEdits }: RowProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const numEditsContainerRef = useRef<HTMLDivElement>(null);
  const numEditsRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const numEditsEl = numEditsRef.current;
    if (!numEditsContainerRef.current || !numEditsEl) {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setIsOverflowing(
        numEditsEl.getBoundingClientRect().width > entry.contentRect.width,
      );
    });
    observer.observe(numEditsContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={styles.row}>
      {avatar}
      <div className={styles.barContainer}>
        <div
          style={{ width: `${Math.round(percentage)}%` }}
          className={styles.bar}
        />
        <div
          style={
            { width: `${100 - Math.round(percentage)}%` } as React.CSSProperties
          }
          className={classNames(styles.numEditsContainer, {
            [styles.isOverflowing]: isOverflowing,
          })}
          ref={numEditsContainerRef}
        >
          <span className={styles.numEdits} ref={numEditsRef}>
            {formatNumEdits(numEdits)}
          </span>
        </div>
      </div>
    </div>
  );
};

interface PostEditorsChartProps {
  edits: DocumentType<typeof POST_INSIGHTS_EDIT_FRAGMENT>[] | null;
}

const PostEditorsChart = ({ edits }: PostEditorsChartProps) => {
  const getUser = useGetUser();
  const compactEdits = _.compact(edits).filter(
    (e): e is SetRequired<SetNonNullable<typeof e, 'user'>, 'user'> =>
      e.user != null,
  );

  const totalEdits = _.sumBy(compactEdits, (edit) => edit.numEdits ?? 0);

  const display = compactEdits.reduce(
    (acc, edit) => {
      const percentage =
        totalEdits === 0 ? 0 : ((edit.numEdits ?? 0) / totalEdits) * 100;

      const editWithPercentage = {
        ...edit,
        percentage,
      };

      if (percentage < 5) {
        return {
          ...acc,
          others: [...acc.others, editWithPercentage],
        };
      }

      return {
        ...acc,
        top: [...acc.top, editWithPercentage],
      };
    },
    {
      top: [],
      others: [],
    } as {
      top: ((typeof compactEdits)[number] & {
        percentage: number;
      })[];
      others: ((typeof compactEdits)[number] & {
        percentage: number;
      })[];
    },
  );

  return (
    <div className={styles.editorChart}>
      {display.top.map((edit) => {
        const user = getUser(edit.user);
        return (
          <Row
            avatar={
              <Tooltip
                content={
                  <Link to={userPath(user)} newTab>
                    <div>{user.name}</div>
                    <Tooltip.Muted>{`${edit.percentage.toFixed(
                      1,
                    )}% of edits`}</Tooltip.Muted>
                  </Link>
                }
                asChild
                trigger="clickOrHover"
              >
                <Avatar size={AVATAR_SIZE} key={user.id} user={user} />
              </Tooltip>
            }
            numEdits={edit.numEdits ?? 0}
            percentage={edit.percentage}
            key={user.id}
          />
        );
      })}
      {display.others.length > 0 && (
        <Row
          avatar={
            <HoverView
              hoverGroupId="additionalEditorsTooltip"
              view={
                <FloatingCard>
                  <AdditionalEditorsTooltipContent edits={display.others} />
                </FloatingCard>
              }
              placement="bottom"
            >
              <GroupCount value={display.others.length} />
            </HoverView>
          }
          numEdits={_.sumBy(display.others, (other) => other.numEdits ?? 0)}
          percentage={_.sumBy(display.others, (other) => other.percentage)}
        />
      )}
      <div className={styles.scale}>
        <div>0%</div>
        <div>25%</div>
        <div>50%</div>
        <div>75%</div>
        <div>100%</div>
      </div>
      <div className={styles.grid} />
    </div>
  );
};

export default PostEditorsChart;
